<template>
  <div class="flex flex-col gap-6">

    <AppPageHeader :title="t('leaveTypes.title')" :description="t('leaveTypes.description')">
      <template #actions>
        <AppButton @click="showAddModal = true">
          <Plus class="h-4 w-4 mr-2" />{{ t('leaveTypes.addLeaveType') }}
        </AppButton>
      </template>
    </AppPageHeader>

    <!-- Filters -->
    <AppCard>
      <div class="p-4 flex flex-wrap gap-3 items-end">
        <div class="flex flex-col gap-1 flex-1 min-w-40">
          <label class="text-xs font-medium text-muted-foreground">Search</label>
          <div class="relative">
            <Search class="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input type="text" v-model="searchQuery" @input="debouncedSearch" :placeholder="t('leaveTypes.searchPlaceholder')" class="h-9 w-full rounded-md border border-input bg-background pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring" />
          </div>
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-xs font-medium text-muted-foreground">Status</label>
          <select v-model="activeFilter" @change="loadLeaveTypes" class="h-9 rounded-md border border-input bg-background px-3 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-ring">
            <option value="">{{ t('leaveTypes.allTypes') }}</option>
            <option value="true">{{ t('leaveTypes.activeOnly') }}</option>
            <option value="false">{{ t('leaveTypes.inactiveOnly') }}</option>
          </select>
        </div>
        <AppButton variant="outline" @click="resetFilters">Reset</AppButton>
      </div>
    </AppCard>

    <!-- List -->
    <AppCard>
      <div class="px-4 py-3 border-b border-border flex items-center justify-between">
        <p class="text-sm font-medium text-foreground">Leave Types ({{ leaveTypes.length }})</p>
        <p class="text-xs text-muted-foreground">Page {{ pagination.page }} of {{ pagination.totalPages }}</p>
      </div>

      <div v-if="loading" class="flex items-center justify-center py-12"><AppSpinner size="lg" /></div>
      <AppEmptyState v-else-if="leaveTypes.length === 0" :icon="Calendar" :title="t('leaveTypes.noLeaveTypes')" :description="t('leaveTypes.noLeaveTypesDesc')" />

      <div v-else class="divide-y divide-border">
        <div v-for="lt in leaveTypes" :key="lt.id" class="p-4 hover:bg-muted/30 transition-colors">
          <div class="flex items-start justify-between gap-4">
            <div class="flex items-start gap-3 flex-1 min-w-0">
              <div class="w-3 h-3 rounded-full mt-1.5 shrink-0" :style="{ backgroundColor: lt.color }"></div>
              <div class="flex-1 min-w-0">
                <div class="flex flex-wrap items-center gap-2 mb-1">
                  <span class="text-sm font-semibold text-foreground">{{ lt.name }}</span>
                  <span v-if="lt.code" class="text-xs px-1.5 py-0.5 rounded bg-muted text-muted-foreground font-mono">{{ lt.code }}</span>
                  <AppBadge :variant="lt.isActive ? 'success' : 'destructive'">{{ lt.isActive ? t('leaveTypes.active') : t('leaveTypes.inactive') }}</AppBadge>
                  <span v-if="lt.genderRestriction !== 'any'" :class="['text-xs px-1.5 py-0.5 rounded font-medium', lt.genderRestriction === 'female' ? 'bg-pink-100 text-pink-800 dark:bg-pink-900/30 dark:text-pink-300' : 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300']">{{ lt.genderRestriction }}</span>
                </div>
                <p v-if="lt.description" class="text-xs text-muted-foreground mb-2">{{ lt.description }}</p>
                <div class="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                  <div><p class="text-muted-foreground">Max / Year</p><p class="font-medium text-foreground">{{ lt.maxPerYear != null ? lt.maxPerYear + ' days' : 'Unlimited' }}</p></div>
                  <div><p class="text-muted-foreground">Carry Forward</p><p class="font-medium text-foreground">{{ lt.carryForward ? (lt.maxCarryover ? 'Up to ' + lt.maxCarryover + 'd' : 'Yes') : 'No' }}</p></div>
                  <div><p class="text-muted-foreground">Accrual Rate</p><p class="font-medium text-foreground">{{ lt.accrualRate != null ? lt.accrualRate + ' d/mo' : 'Manual' }}</p></div>
                  <div v-if="lt.statistics"><p class="text-muted-foreground">Total Requests</p><p class="font-medium text-foreground">{{ lt.statistics.totalRequests }}</p></div>
                </div>
              </div>
            </div>
            <div class="flex items-center gap-1 shrink-0">
              <AppButton size="icon-sm" variant="ghost" title="Edit" @click="editLeaveType(lt)"><Edit class="h-3.5 w-3.5" /></AppButton>
              <AppButton size="icon-sm" variant="ghost" class="text-destructive" title="Delete" @click="confirmDelete(lt)"><Trash2 class="h-3.5 w-3.5" /></AppButton>
            </div>
          </div>
        </div>
      </div>

      <div v-if="pagination.totalPages > 1" class="flex items-center justify-between px-4 py-3 border-t border-border">
        <span class="text-sm text-muted-foreground">{{ ((pagination.page-1)*pagination.limit)+1 }}–{{ Math.min(pagination.page*pagination.limit, pagination.total) }} of {{ pagination.total }}</span>
        <div class="flex items-center gap-1">
          <AppButton size="icon-sm" variant="outline" :disabled="pagination.page === 1" @click="changePage(pagination.page-1)"><ChevronLeft class="h-3.5 w-3.5" /></AppButton>
          <button v-for="p in getVisiblePages()" :key="p" @click="changePage(p)" :class="['h-8 min-w-[2rem] px-2 rounded-md text-sm font-medium transition-colors', p === pagination.page ? 'bg-primary text-primary-foreground' : 'border border-border text-foreground hover:bg-accent']">{{ p }}</button>
          <AppButton size="icon-sm" variant="outline" :disabled="pagination.page === pagination.totalPages" @click="changePage(pagination.page+1)"><ChevronRight class="h-3.5 w-3.5" /></AppButton>
        </div>
      </div>
    </AppCard>

    <!-- Add/Edit Modal -->
    <AppModal v-model="showLeaveTypeModal" :title="showEditModal ? t('leaveTypes.editLeaveTypeTitle') : t('leaveTypes.addLeaveTypeTitle')" size="lg">
      <form @submit.prevent="saveLeaveType" class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-foreground">{{ t('leaveTypes.name') }} <span class="text-destructive">*</span></label>
          <input type="text" v-model="leaveTypeForm.name" required placeholder="e.g., Annual Leave" class="h-9 rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring" />
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-foreground">{{ t('leaveTypes.code') }}</label>
          <input type="text" v-model="leaveTypeForm.code" placeholder="e.g., ANNUAL" class="h-9 rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring" />
        </div>
        <div class="md:col-span-2 flex flex-col gap-1">
          <label class="text-sm font-medium text-foreground">{{ t('leaveTypes.descriptionLabel') }}</label>
          <textarea v-model="leaveTypeForm.description" rows="2" class="rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring resize-none"></textarea>
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-foreground">{{ t('leaveTypes.color') }}</label>
          <div class="flex items-center gap-2">
            <input type="color" v-model="leaveTypeForm.color" class="h-9 w-14 rounded-md border border-input bg-background p-1 cursor-pointer" />
            <input type="text" v-model="leaveTypeForm.color" class="h-9 flex-1 rounded-md border border-input bg-background px-3 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-ring" />
          </div>
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-foreground">Gender Restriction</label>
          <select v-model="leaveTypeForm.genderRestriction" class="h-9 rounded-md border border-input bg-background px-3 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-ring">
            <option value="any">Any (All Employees)</option>
            <option value="male">Male Only</option>
            <option value="female">Female Only</option>
          </select>
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-foreground">{{ t('leaveTypes.maxPerYear') }}</label>
          <input type="number" v-model="leaveTypeForm.maxPerYear" min="0" :placeholder="t('leaveTypes.unlimited')" class="h-9 rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring" />
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-foreground">{{ t('leaveTypes.accrualRate') }}</label>
          <input type="number" v-model="leaveTypeForm.accrualRate" step="0.01" min="0" :placeholder="t('leaveTypes.manual')" class="h-9 rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring" />
        </div>
        <div class="md:col-span-2 flex flex-col gap-3">
          <label class="flex items-center gap-2 cursor-pointer text-sm">
            <input type="checkbox" v-model="leaveTypeForm.carryForward" class="rounded" />
            <span class="text-foreground">{{ t('leaveTypes.carryForward') }}</span>
          </label>
          <div v-if="leaveTypeForm.carryForward" class="flex flex-col gap-1 ml-6">
            <label class="text-xs font-medium text-muted-foreground">{{ t('leaveTypes.upTo') }}</label>
            <input type="number" v-model="leaveTypeForm.maxCarryover" min="0" :placeholder="t('leaveTypes.unlimited')" class="h-9 w-40 rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring" />
          </div>
          <label class="flex items-center gap-2 cursor-pointer text-sm">
            <input type="checkbox" v-model="leaveTypeForm.isActive" class="rounded" />
            <span class="text-foreground">{{ t('leaveTypes.active') }}</span>
          </label>
        </div>
      </form>
      <template #footer>
        <div class="flex justify-end gap-2">
          <AppButton variant="outline" @click="closeModal">{{ t('leaveTypes.cancel') }}</AppButton>
          <AppButton :disabled="submitting" @click="saveLeaveType">
            {{ submitting ? t('common.loading') : (showEditModal ? t('leaveTypes.editLeaveTypeTitle') : t('leaveTypes.addLeaveTypeTitle')) }}
          </AppButton>
        </div>
      </template>
    </AppModal>

    <!-- Delete Modal -->
    <AppModal v-model="showDeleteModal" title="Delete Leave Type" size="sm">
      <div class="flex gap-3">
        <AlertTriangle class="h-5 w-5 text-destructive shrink-0 mt-0.5" />
        <p class="text-sm text-muted-foreground">Are you sure you want to delete <strong class="text-foreground">{{ leaveTypeToDelete?.name }}</strong>? This cannot be undone.</p>
      </div>
      <template #footer>
        <div class="flex justify-end gap-2">
          <AppButton variant="outline" @click="showDeleteModal = false">Cancel</AppButton>
          <AppButton variant="destructive" :disabled="deleting" @click="deleteLeaveType">{{ deleting ? 'Deleting…' : 'Delete' }}</AppButton>
        </div>
      </template>
    </AppModal>

  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  Plus, Search, Calendar, Edit, Trash2, AlertTriangle,
  ChevronLeft, ChevronRight
} from 'lucide-vue-next';

import { AppPageHeader, AppCard, AppBadge, AppButton, AppModal, AppEmptyState, AppSpinner } from '@/components/ui';
import adminLeaveTypesService from '@/services/adminLeaveTypesService';

const { t } = useI18n();
const loading = ref(false);
const submitting = ref(false);
const deleting = ref(false);
const leaveTypes = ref([]);

const searchQuery = ref('');
const activeFilter = ref('');
const pagination = ref({ page: 1, limit: 10, total: 0, totalPages: 0 });

const showAddModal = ref(false);
const showEditModal = ref(false);
const showDeleteModal = ref(false);
const editingLeaveType = ref(null);
const leaveTypeToDelete = ref(null);

const leaveTypeForm = ref({
  name: '', code: '', description: '', color: '#3B82F6',
  maxPerYear: null, carryForward: false, accrualRate: null,
  maxCarryover: null, genderRestriction: 'any', isActive: true
});

const showLeaveTypeModal = computed({
  get: () => showAddModal.value || showEditModal.value,
  set: (v) => { if (!v) closeModal(); }
});

const loadLeaveTypes = async () => {
  try {
    loading.value = true;
    const params = { page: pagination.value.page, limit: pagination.value.limit, search: searchQuery.value, active: activeFilter.value };
    const response = await adminLeaveTypesService.getLeaveTypes(params);
    leaveTypes.value = response.data?.leaveTypes || [];
    if (response.data?.pagination) pagination.value = response.data.pagination;
  } catch (error) {
    console.error('Error loading leave types:', error);
    leaveTypes.value = [];
  } finally {
    loading.value = false;
  }
};

const debouncedSearch = debounce(() => { pagination.value.page = 1; loadLeaveTypes(); }, 500);
const resetFilters = () => { searchQuery.value = ''; activeFilter.value = ''; pagination.value.page = 1; loadLeaveTypes(); };
const changePage = (page) => { pagination.value.page = page; loadLeaveTypes(); };

const getVisiblePages = () => {
  const current = pagination.value.page;
  const total = pagination.value.totalPages;
  const delta = 2;
  const range = [];
  for (let i = Math.max(2, current - delta); i <= Math.min(total - 1, current + delta); i++) range.push(i);
  if (current - delta > 2) range.unshift('...');
  if (current + delta < total - 1) range.push('...');
  range.unshift(1);
  if (total > 1) range.push(total);
  return range.filter((page, index, array) => array.indexOf(page) === index);
};

const editLeaveType = (lt) => {
  editingLeaveType.value = lt;
  leaveTypeForm.value = {
    name: lt.name, code: lt.code, description: lt.description, color: lt.color,
    maxPerYear: lt.maxPerYear, carryForward: lt.carryForward, accrualRate: lt.accrualRate,
    maxCarryover: lt.maxCarryover, genderRestriction: lt.genderRestriction, isActive: lt.isActive
  };
  showEditModal.value = true;
};

const confirmDelete = (lt) => { leaveTypeToDelete.value = lt; showDeleteModal.value = true; };

const saveLeaveType = async () => {
  try {
    submitting.value = true;
    if (showEditModal.value) {
      await adminLeaveTypesService.updateLeaveType(editingLeaveType.value.id, leaveTypeForm.value);
    } else {
      await adminLeaveTypesService.createLeaveType(leaveTypeForm.value);
    }
    closeModal();
    await loadLeaveTypes();
  } catch (error) {
    console.error('Error saving leave type:', error);
  } finally {
    submitting.value = false;
  }
};

const deleteLeaveType = async () => {
  try {
    deleting.value = true;
    await adminLeaveTypesService.deleteLeaveType(leaveTypeToDelete.value.id);
    showDeleteModal.value = false;
    leaveTypeToDelete.value = null;
    await loadLeaveTypes();
  } catch (error) {
    console.error('Error deleting leave type:', error);
  } finally {
    deleting.value = false;
  }
};

const closeModal = () => {
  showAddModal.value = false;
  showEditModal.value = false;
  editingLeaveType.value = null;
  leaveTypeForm.value = { name: '', code: '', description: '', color: '#3B82F6', maxPerYear: null, carryForward: false, accrualRate: null, maxCarryover: null, genderRestriction: 'any', isActive: true };
};

function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => { clearTimeout(timeout); func(...args); };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

onMounted(() => loadLeaveTypes());
</script>
